import axios from 'axios';
import { clearAuthTokens, getAccessToken, getRefreshToken } from '@/shared/session/auth';
import { externalConnections, LOGOUT_REDIRECT_URI_KEY } from '@/constants/Constants';
import { buildCurrentPageUrl } from '@/shared/utils/url.util';
import { logger } from '@/shared/libs/logger';

type LogoutReason = 'user_initiated' | 'session_expired' | 'auth_error';

// Caps the revoke call so a slow login API can't hold the user on the loader.
const REVOKE_TIMEOUT_MS = 5_000;

let logoutStarted = false;

export function hasLogoutStarted(): boolean {
  return logoutStarted;
}

export async function logout({
  reason,
  context,
}: {
  reason: LogoutReason;
  /** Extra telemetry on why the session ended. */
  context?: Record<string, unknown>;
}) {
  // Parallel failing requests and the expiry check can all land here; only the first redirects.
  if (logoutStarted) {
    return;
  }
  logoutStarted = true;

  // Resolved before anything is awaited: the app can still navigate while the
  // revoke call is in flight, and the user should come back to where they were.
  const returnTo = reason === 'user_initiated' ? null : buildCurrentPageUrl();

  logger.info('Session ended', { reason, ...context });

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  clearAuthTokens();

  // /v1/logout requires both; skip when either is missing to avoid a guaranteed 401.
  // tg-login-service's logout doesn't revoke these tokens, so this runs before either logout.
  if (accessToken && refreshToken) {
    try {
      await axios.post(
        externalConnections.loginApiLogout,
        { refresh_token: refreshToken },
        { headers: { Authorization: `Bearer ${accessToken}` }, timeout: REVOKE_TIMEOUT_MS },
      );
    } catch (error) {
      logger.error('Error revoking token', undefined, error);
    }
  }

  if (externalConnections.loginServiceLogout) {
    postLogout(buildLogoutUrl(returnTo, externalConnections.loginServiceLogout));
    return;
  }

  globalThis.location.replace(buildLogoutUrl(returnTo));
}

export function buildLogoutUrl(
  redirectUri: string | null,
  logoutUrlString: string = externalConnections.logout,
): string {
  if (redirectUri !== null && URL.canParse(redirectUri) && URL.canParse(logoutUrlString)) {
    const logoutUrl = new URL(logoutUrlString);
    logoutUrl.searchParams.set(LOGOUT_REDIRECT_URI_KEY, redirectUri);
    return logoutUrl.toString();
  }

  return logoutUrlString;
}

/**
 * tg-login-service only accepts a logout as a top-level POST from a page of this environment, which it checks with
 * the Origin header. Unlike tg-login-ui's logout, it also ends the TG session and expires its cookie.
 */
function postLogout(action: string): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  document.body.append(form);
  form.submit();
}

export default logout;
